import { BrandPlate } from "../../components/BrandMark";
import { ThemeToggle } from "../../components/ThemeToggle";
import { EmailLinkCompletionScreen } from "./EmailLinkCompletionScreen";
import { SignInControls } from "./SignInControls";
import { useEmailLinkCompletion } from "./use-email-link-completion";

/**
 * The signed-out screen, with the control it exists for.
 *
 * This component is the template's answer to a failure it has already caused
 * once: an app whose signed-out screen said "sign in to continue" and offered
 * nothing to click. The e2e suite signs in through the emulator bridge, so no
 * test noticed. Hence both the control below and the test that asserts it.
 *
 * The control itself lives in SignInControls, because the event microsite needs
 * the same one — a signed-out invitee lands there, not here.
 *
 * A returning email link is completed here, not inside the control, so that
 * the page itself can become the "Signing you in" state until Firebase
 * reports the user.
 */
export function SignInScreen({
  appName = "Holtzbrinck Connect",
  tagline,
}: {
  appName?: string;
  tagline?: string;
}) {
  const { completion, confirmAddress, retry, startOver } =
    useEmailLinkCompletion();

  if (completion.phase === "pending" || completion.phase === "failed") {
    return (
      <EmailLinkCompletionScreen
        state={completion}
        onRetry={retry}
        onStartOver={startOver}
      />
    );
  }

  return (
    <main className="relative flex min-h-screen items-center justify-center bg-gradient-page p-6">
      <ThemeToggle className="absolute right-3 top-3" />
      <div className="w-full max-w-md rounded-xl border border-border bg-card p-8 text-center text-card-foreground shadow-card dark:border-edge dark:border-lit-edge dark:shadow-lit">
        <BrandPlate className="mx-auto mb-5" />
        <h1 className="font-display text-2xl font-semibold text-foreground">
          {appName}
        </h1>
        {tagline && (
          <p className="mt-2 text-sm text-muted-foreground">{tagline}</p>
        )}
        <p className="mb-6 mt-4 text-muted-foreground">
          Sign in with your work account to continue.
        </p>

        <SignInControls
          needsAddress={completion.phase === "needs-address"}
          onConfirmAddress={confirmAddress}
        />
        <p className="mt-6 text-xs text-muted-foreground">
          Holtzbrinck Publishing Group
        </p>
      </div>
    </main>
  );
}
